import { useQuery } from "@tanstack/react-query";
import api, { fmtINR } from "@/lib/api";
import { useOutletContext } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/Shared";
import { Celebrations } from "@/components/Celebrations";
import { Card } from "@/components/ui/card";
import {
  Users, FileText, Contact, Clock, Wallet, LogOut, TrendingUp, Loader2,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from "recharts";

function Stat({ icon: Icon, label, value, tint, testid }) {
  return (
    <Card className="p-5 border border-border rounded-xl bg-card shadow-sm hover:shadow-md transition-shadow" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-muted-foreground">{label}</p>
          <p className="font-heading text-3xl font-extrabold mt-2 tracking-tight">{value}</p>
        </div>
        <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${tint}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </Card>
  );
}

export default function Dashboard() {
  const { role } = useOutletContext();
  const { user } = useAuth();
  const { data, isLoading } = useQuery({
    queryKey: ["dashboard", role],
    queryFn: async () => (await api.get("/dashboard/stats")).data,
  });

  if (isLoading || !data)
    return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-muted-foreground" /></div>;

  if (data.role === "hr") {
    const pipeline = Object.entries(data.pipeline || {}).map(([name, count]) => ({ name, count }));
    const colors = ["#64748b", "#2563eb", "#7c3aed", "#059669", "#e11d48"];
    return (
      <div>
        <PageHeader title="Dashboard" subtitle="Organisation-wide HR overview and hiring pipeline." />
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
          <Stat icon={Users} label="Candidates" value={data.candidates} tint="bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400" testid="stat-candidates" />
          <Stat icon={FileText} label="Offers" value={data.offers} tint="bg-violet-50 text-violet-600 dark:bg-violet-950/50 dark:text-violet-400" testid="stat-offers" />
          <Stat icon={Contact} label="Employees" value={data.employees} tint="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400" testid="stat-employees" />
          <Stat icon={Clock} label="In Today" value={data.checked_in_today} tint="bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400" testid="stat-checkedin" />
          <Stat icon={Wallet} label="Payslips" value={data.payslips} tint="bg-teal-50 text-teal-600 dark:bg-teal-950/50 dark:text-teal-400" testid="stat-payslips" />
          <Stat icon={LogOut} label="Exits" value={data.relieving} tint="bg-rose-50 text-rose-600 dark:bg-rose-950/50 dark:text-rose-400" testid="stat-exits" />
        </div>
        <div className="grid lg:grid-cols-3 gap-6">
        <Card className="p-6 border border-border rounded-xl lg:col-span-2">
          <div className="flex items-center gap-2 mb-6">
            <TrendingUp className="w-5 h-5 text-accent" />
            <h3 className="font-heading text-lg font-semibold">Recruitment Pipeline</h3>
          </div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={pipeline}>
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={false} tickLine={false} allowDecimals={false} />
              <Tooltip cursor={{ fill: "rgba(148,163,184,0.1)" }} contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                {pipeline.map((_, i) => <Cell key={i} fill={colors[i % colors.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
        <Celebrations items={data.celebrations} />
        </div>
      </div>
    );
  }

  // Employee dashboard
  const emp = data.employee;
  return (
    <div>
      <PageHeader title={`Welcome, ${user.name.split(" ")[0]} 👋`} subtitle="Your personal HR dashboard." />
      {emp ? (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-8">
          <Stat icon={Contact} label="Employee ID" value={emp.employee_id} tint="bg-blue-50 text-blue-600 dark:bg-blue-950/50 dark:text-blue-400" />
          <Stat icon={Wallet} label="Annual CTC" value={fmtINR(emp.annual_ctc)} tint="bg-emerald-50 text-emerald-600 dark:bg-emerald-950/50 dark:text-emerald-400" />
          <Stat icon={Wallet} label="Net / Month" value={fmtINR(emp.breakdown.net_monthly)} tint="bg-teal-50 text-teal-600 dark:bg-teal-950/50 dark:text-teal-400" />
          <Stat icon={Clock} label="Today" value={data.attendance_today?.status || "—"} tint="bg-amber-50 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400" />
        </div>
      ) : (
        <Card className="p-6">No employee profile linked to your account yet.</Card>
      )}
      {emp && (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card className="p-6 border border-border rounded-xl lg:col-span-2">
            <h3 className="font-heading text-lg font-semibold mb-1">{emp.designation}</h3>
            <p className="text-muted-foreground text-sm">{emp.department} • {emp.location} • Joined {emp.doj}</p>
          </Card>
          <Celebrations items={data.celebrations} />
        </div>
      )}
    </div>
  );
}
